import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import WithRouter from "../../../hoc/WithRouter";
import Buttons from "../../../components/UI/Buttons";
import PERMISSIONS from "../../../utils/AclPermissions";
import { isSet } from "../../../utils/commonUtils";
import VisitDetailView from "../../EmployeeVisitContainer/VisitDetails";
import VisitLOS from "./VisitLOS";
import "./NewEmrHeader.css";
import PhoneIcon from "../../../components/UI/Icons/PhoneIcon";
import VisitsService from "../../../services/VisitsService";
import DaisyBillService from "../../../services/DaisyBillService";
import EmrVisitsService from "../../../services/EmrVisitsService";
import { success, error } from "../../../store/CommonSlice";
// Manual document upload modal - commented out, auto-upload chart note is used instead
// import DaisyBillDocumentUploadModal from "../../../components/Modal/DaisyBillDocumentUploadModal";

const BreadCrumbs = (props) => {
  const dispatch = useDispatch();
  const {
    employeeDetails,
    visit_category,
    visitDetails,
    visitStatus = "",
    timelineData = [],
    visit_id,
    onOpenSendMessageFromCharting,
  } = props;
  const PatientName = (employeeDetails?.first_name && employeeDetails?.last_name)
  ? `${employeeDetails.first_name} ${employeeDetails.last_name}`
  : ""; 
  const [viewModelOpen, setViewModelOpen] = useState(false);
  const [isSubmittingToDaisyBill, setIsSubmittingToDaisyBill] = useState(false);
  const [daisyBillDocumentModalOpen, setDaisyBillDocumentModalOpen] = useState(false);
  const [selectedBillForDocument, setSelectedBillForDocument] = useState(null);
  const [selectedVisitInfoForDocument, setSelectedVisitInfoForDocument] = useState(null);
  
  // Local state for LOS time - isolated from parent state to prevent triggering other API calls
  const [losTime, setLosTime] = useState(() => {
    // Initialize from visitDetails on mount
    return visitDetails?.current_los_time !== null && visitDetails?.current_los_time !== undefined
      ? visitDetails.current_los_time
      : (visitDetails?.los_time !== null && visitDetails?.los_time !== undefined 
        ? visitDetails.los_time 
        : 0);
  });
  
  // Sync with visitDetails only on initial load or when visitDetails is completely replaced
  // This prevents updates from our own refresh calls from triggering parent re-renders
  useEffect(() => {
    const newLosTime = visitDetails?.current_los_time !== null && visitDetails?.current_los_time !== undefined
      ? visitDetails.current_los_time
      : (visitDetails?.los_time !== null && visitDetails?.los_time !== undefined 
        ? visitDetails.los_time 
        : 0);
    
    // Only update if the value is significantly different (more than 1 minute)
    // This prevents micro-updates from causing cascading re-renders
    if (Math.abs(newLosTime - losTime) > 1) {
      setLosTime(newLosTime);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visitDetails?.current_los_time, visitDetails?.los_time]);
  
  const viewPatientDetails = () => {
    setViewModelOpen(!viewModelOpen);
    props.fetchVisitList(props.visit_id);
  };
  
  // Check if visit is discharged
  const isDischarged = visitStatus === 'Complete & Discharged' || visitStatus === 'Discharged';
  
  // Check if current status is a start status (Waiting Room or Checked In)
  const currentStatusIsStart = visitStatus === 'Waiting Room' || visitStatus === 'Checked In';
  
  // Check if visit has started - more inclusive logic:
  // 1. Status is not "New" (visit has progressed)
  // 2. OR has timeline entries (visit has activity)
  // 3. OR has los_time value (LOS was calculated)
  // 4. OR has Waiting Room/Checked In status in timeline or current status
  const hasTimelineStartStatus = timelineData && timelineData.length > 0 && timelineData.some(
    (item) => item.visit_status === 'Checked In' || item.visit_status === 'Waiting Room'
  );
  
  const hasLosTime = (visitDetails?.los_time !== null && visitDetails?.los_time !== undefined) ||
                     (visitDetails?.current_los_time !== null && visitDetails?.current_los_time !== undefined);
  
  const hasTimelineEntries = timelineData && timelineData.length > 0;
  
  // Show LOS bar if visit has started (not "New") and either:
  // - Has timeline start status (Waiting Room/Checked In)
  // - Has timeline entries (visit has activity)
  // - Has LOS time calculated
  // - Current status is a start status
  const hasStartStatus = visitStatus !== 'New' && (
    hasTimelineStartStatus || 
    currentStatusIsStart || 
    hasTimelineEntries || 
    hasLosTime
  );
  
  // Visit is running only if status is "Waiting Room" or "Checked In" and not discharged
  const isRunning = currentStatusIsStart && !isDischarged;
  
  // Check if visit is auto-discharged (LOS > 180 and status is Complete & Discharged)
  const isAutoDischarged = losTime > 180 && visitStatus === 'Complete & Discharged';
  
  // Lightweight refresh function that only updates local LOS time - doesn't touch parent state
  const refreshLosTime = async () => {
    if (!props.visit_id) return;
    try {
      const response = await VisitsService.getVisitLosTime(props.visit_id);
      if (response && response.current_los_time !== undefined) {
        // Update only local state - this won't trigger parent component updates
        setLosTime(response.current_los_time);
        
        // If LOS is above 180, update visit status to "Complete & Discharged"
        if (response.current_los_time > 180 && visitStatus !== 'Complete & Discharged') {
          try {
            await VisitsService.updateVisitStatus(null, props.visit_id, {
              visit_status: "Complete & Discharged"
            });
            // Refresh visit details to update visit_status in parent component
            // This will update the dropdown in SubTabSection
            if (props.fetchVisitList) {
              await props.fetchVisitList(props.visit_id);
            }
          } catch (statusError) {
            console.error('Error updating visit status:', statusError);
          }
        }
      }
    } catch (error) {
      console.error('Error refreshing LOS time:', error);
    }
  };

  // Handle Daisy Bill submission
  const handleSubmitToDaisyBill = async () => {
    if (!props.visit_id) {
      dispatch(error("Visit ID is missing"));
      return;
    }

    if (
      visit_category !== "Work Comp - New Injury" &&
      visit_category !== "Work Comp - Follow Up" &&
      visit_category !== "Work Comp - Physical Therapy"
    ) {
      dispatch(
        error(
          "This feature is only available for Work Comp - New Injury, Work Comp - Follow Up, or Work Comp - Physical Therapy visits"
        )
      );
      return;
    }

    setIsSubmittingToDaisyBill(true);
    props.Loader?.(true);

    try {
      const result = await DaisyBillService.processNewInjuryBilling(props.visit_id);
      
      // Handle different response structures
      // Success: { success: true, message: "...", patient_id: ..., injury_id: ..., bill_id: ... }
      // Error from service: { code: 400, data: { success: false, message: "..." } }
      // Error direct: { success: false, message: "..." }
      
      let responseData = result;
      
      // Check if response is wrapped in data property (error response structure)
      if (result?.data && result.data.success !== undefined) {
        responseData = result.data;
      }
      
      if (responseData && responseData.success) {
        // Success case
        const successMessage = responseData.message || "Successfully processed billing in Daisy Bill";
        const details = [];
        
        if (responseData.patient_id) {
          details.push(`Patient ID: ${responseData.patient_id}`);
        }
        if (responseData.injury_id) {
          details.push(`Injury ID: ${responseData.injury_id}`);
        }
        if (responseData.bill_id) {
          details.push(`Bill ID: ${responseData.bill_id}`);
        }
        
        const fullMessage = details.length > 0 
          ? `${successMessage}. ${details.join(", ")}`
          : successMessage;
        
        dispatch(success(fullMessage, 6000)); // 6 seconds timeout
        
        // Update billing status to "Submited to Daisy bill"
        if (props.visit_id) {
          console.log('updateVisitBillingStatus',props.visit_id);
          try {
            await VisitsService.updateVisitBillingStatus(props.visit_id, {
              billing_status: "Submited to Daisy bill"
            });
          } catch (error) {
            console.error("Error updating billing status:", error);
          }
        }
        
        // Auto-upload chart note to Daisy Bill (replaces manual document upload modal)
        if (responseData.bill_id && props.visit_id) {
          try {
            const pdfResponse = await EmrVisitsService.getCombinedChartNotePdf(props.visit_id, true);
            const base64Data = typeof pdfResponse === "string" ? pdfResponse : pdfResponse?.data ?? pdfResponse;
            if (base64Data && typeof base64Data === "string" && base64Data.length > 0) {
              const attachResult = await DaisyBillService.attachDocumentToBill(
                responseData.bill_id,
                base64Data,
                "M1" // M1 - Medical Record Attachment (Daisy Bill report type)
              );
              if (attachResult?.success) {
                dispatch(success("Document uploaded successfully to Daisy Bill", 4000));
              } else {
                dispatch(error(attachResult?.message || "Chart note upload failed", 6000));
              }
            } else {
              dispatch(error("Could not generate chart note PDF for upload", 6000));
            }
          } catch (uploadErr) {
            console.error("Error auto-uploading chart note to Daisy Bill:", uploadErr);
            const uploadErrMsg = uploadErr?.response?.data?.data?.message || uploadErr?.message || "Failed to upload chart note";
            dispatch(error(uploadErrMsg, 6000));
          }
        }
        // --- Manual document upload modal (commented out - auto-upload used instead) ---
        // if (responseData.bill_id) {
        //   const visitInfo = {
        //     patientName: PatientName,
        //     visitDate: visitDetails?.visit_date || "N/A",
        //     visitTime: visitDetails?.visit_time || "N/A"
        //   };
        //   setSelectedBillForDocument(responseData.bill_id);
        //   setSelectedVisitInfoForDocument(visitInfo);
        //   setDaisyBillDocumentModalOpen(true);
        // }
      } else {
        // Failure case - extract error message from various structures
        let errorMessage = "Failed to submit to Daisy Bill. Please try again.";
        
        // Check if validation errors exist (422 status)
        if (responseData?.errors) {
          errorMessage = formatValidationErrors(responseData.errors, responseData.message);
        }
        // Check result.data.message (error response: { code: 400, data: { success: false, message: "..." } })
        else if (result?.data?.message) {
          errorMessage = result.data.message;
        }
        // Check responseData.message (direct error: { success: false, message: "..." })
        else if (responseData?.message) {
          errorMessage = responseData.message;
        }
        // Check result.message (fallback)
        else if (result?.message) {
          errorMessage = result.message;
        }
        
        dispatch(error(errorMessage, 10000)); // 10 seconds timeout
        console.error("Daisy Bill submission error:", result);
      }
    } catch (error) {
      console.error("Error submitting to Daisy Bill:", error);
      
      // Extract error message from various possible error structures
      let errorMessage = "An error occurred while submitting to Daisy Bill";
      
      if (error?.response?.data) {
        const errorData = error.response.data;
        // Check if validation errors exist (422 status)
        if (errorData?.data?.errors) {
          errorMessage = formatValidationErrors(errorData.data.errors, errorData.data.message);
        }
        // Handle: { code: 400, data: { success: false, message: "..." } }
        else if (errorData?.data?.message) {
          errorMessage = errorData.data.message;
        } 
        // Handle: { code: 400, message: "..." }
        else if (errorData?.message) {
          errorMessage = errorData.message;
        } 
        // Handle: { code: 400, data: "..." } (string)
        else if (errorData?.data) {
          errorMessage = typeof errorData.data === 'string' 
            ? errorData.data 
            : errorData.data.message || errorMessage;
        }
      } else if (error?.message) {
        errorMessage = error.message;
      }
      
      dispatch(error(errorMessage, 6000)); // 6 seconds timeout
    } finally {
      setIsSubmittingToDaisyBill(false);
      props.Loader?.(false);
    }
  };

  // Format validation errors into a readable message
  const formatValidationErrors = (errors, baseMessage = "Please fix the following validation errors:") => {
    if (!errors || typeof errors !== 'object') {
      return baseMessage;
    }

    let errorMessages = [];

    // Patient errors
    if (errors.patient) {
      Object.keys(errors.patient).forEach(field => {
        const fieldErrors = Array.isArray(errors.patient[field]) 
          ? errors.patient[field] 
          : [errors.patient[field]];
        fieldErrors.forEach(err => {
          errorMessages.push(`Patient ${field.replace(/_/g, ' ')}: ${err}`);
        });
      });
    }

    // Injury errors
    if (errors.injury) {
      Object.keys(errors.injury).forEach(field => {
        const fieldErrors = Array.isArray(errors.injury[field]) 
          ? errors.injury[field] 
          : [errors.injury[field]];
        fieldErrors.forEach(err => {
          errorMessages.push(`Injury ${field.replace(/_/g, ' ')}: ${err}`);
        });
      });
    }

    // Bill errors
    if (errors.bill) {
      Object.keys(errors.bill).forEach(field => {
        if (field === 'orders' && Array.isArray(errors.bill[field])) {
          errors.bill[field].forEach(err => {
            errorMessages.push(`Bill Orders: ${err}`);
          });
        } else {
          const fieldErrors = Array.isArray(errors.bill[field]) 
            ? errors.bill[field] 
            : [errors.bill[field]];
          fieldErrors.forEach(err => {
            errorMessages.push(`Bill ${field.replace(/_/g, ' ')}: ${err}`);
          });
        }
      });
    }

    // Other errors (including followup_mapping, incident, etc.)
    Object.keys(errors).forEach(key => {
      if (!['patient', 'injury', 'bill'].includes(key)) {
        const fieldErrors = Array.isArray(errors[key]) 
          ? errors[key] 
          : [errors[key]];
        fieldErrors.forEach(err => {
          // Format followup_mapping error more clearly
          if (key === 'followup_mapping') {
            errorMessages.push(err);
          } else {
            // Format other keys (replace underscores with spaces, capitalize)
            const formattedKey = key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
            errorMessages.push(`${formattedKey}: ${err}`);
          }
        });
      }
    });

    // Join all errors, each on a new line with bullet points
    return errorMessages.length > 0 
      ? `${baseMessage}\n\n${errorMessages.map(err => `• ${err}`).join('\n')}`
      : baseMessage;
  };

  return (
    <>
      {viewModelOpen ? (
        <VisitDetailView
          isOpen={true}
          visitId={props.visit_id}
          modalToogleHandler={viewPatientDetails}
          fetchDetails={() => props.fetchVisitList(props.visit_id)}
          snapEmployeeDetails={visitDetails.employee}
        />
      ) : (
        ""
      )}
      <div className="sh_cardHeader pb-0">
        <div className="d-flex justify-content-between align-items-center">
          <div className="d-flex flex-column w-100">
            {/* Breadcrumb Navigation */}
            <nav aria-label="breadcrumb" className="breadcrumbs">
              <ul className="breadcrumb mb-0 px-0 py-1">
                <li className="breadcrumb-item active px-0">
                  <Buttons
                    className="pr-2 pl-0"
                    label={
                      isSet(props.params.backTo, null) != null
                        ? `${props.params.backTo}`
                        : "visits"
                    }
                    acl={PERMISSIONS.VISITS.LIST}
                    href={
                      isSet(props.params.backTo, null) != null
                        ? `/${props.params.backTo}`
                        : "/visits"
                    }
                  />
                </li>
                <li className="breadcrumb-item active px-0">
                  <a href="#" className="px-2">
                    Patient
                  </a>
                </li>
                <li className="breadcrumb-item px-0" aria-current="page">
                  <a href="#" className="px-2">
                    {PatientName}
                  </a>
                </li>
              </ul>
            </nav>

            {/* Header Row: Patient Info (left) + Visit LOS + View More (right) */}
            <div className="d-flex justify-content-between align-items-center mt-1 w-100 emr-header-row">
              {/* Left: Patient Name + Badge */}
              <div className="d-flex align-items-center emr-header-left">
                <h2 className="h2-strong mb-0">{PatientName}</h2>
                <span className="badge mx-2">{visit_category}</span>
              </div>

              {/* Right: Visit LOS + View More Button */}
              <div className="d-flex align-items-center emr-header-right">
                {visit_id && typeof onOpenSendMessageFromCharting === "function" && (
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => onOpenSendMessageFromCharting()}
                    title="Send a message"
                    aria-label="Send message from charting"
                    style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}
                  >
                    <span className="d-inline-flex align-items-center" aria-hidden>
                      <PhoneIcon color="#FFFFFF" size={20} />
                    </span>
                    Message
                  </button>
                )}
              {(visit_category === "Work Comp - New Injury" ||
                visit_category === "Work Comp - Follow Up" ||
                visit_category === "Work Comp - Physical Therapy") && (
                  <Buttons
                    className="btn btn-primary mr-4"
                    accessType="Hide"
                    label={
                      isSubmittingToDaisyBill ? (
                        <>
                          <span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
                          Submitting...
                        </>
                      ) : (
                        "Submit to Daisy Bill"
                      )
                    }
                    acl={PERMISSIONS.VISITS.SUBMIT_TO_DAISY_BILL}
                    clickHandler={handleSubmitToDaisyBill}
                    disabled={isSubmittingToDaisyBill}
                  />
                )}
                {![
                  "Work Comp - Physical Therapy",
                  "Work Comp - Other",
                  "Work Comp - New Injury",
                  "Work Comp - Follow Up",
                  "Urgent Care - Labs",
                  "Urgent Care - Insurance",
                  "Urgent Care - COVID-19 Testing",
                  "Urgent Care - Cash Patient",
                  "Urgent Care - Labs Only",
                  "Urgent Care - Illness",
                  "Urgent Care - Injury",
                  "Urgent Care - Physicals",
                  "Urgent Care - Drug Screen"
                ].includes(visit_category) && (
                  <Buttons
                    className="btn btn-primary mr-4"
                    label="Visit Details"
                    acl="any"
                    clickHandler={() => setViewModelOpen(true)}
                  />
                )}
                {hasStartStatus && (
                  <div className="emr-visit-los-slot">
                    <VisitLOS 
                      value={losTime} 
                      min={0} 
                      max={visitDetails?.max_los_time || 90}
                      isRunning={isRunning}
                      onRefresh={refreshLosTime}
                      isAutoDischarged={isAutoDischarged}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Daisy Bill Document Upload Modal - commented out, auto-upload chart note used instead */}
      {/* {daisyBillDocumentModalOpen && selectedBillForDocument && (
        <DaisyBillDocumentUploadModal
          isOpen={daisyBillDocumentModalOpen}
          billId={selectedBillForDocument}
          visitInfo={selectedVisitInfoForDocument}
          modalToogleHandler={() => {
            setDaisyBillDocumentModalOpen(false);
            setSelectedBillForDocument(null);
            setSelectedVisitInfoForDocument(null);
          }}
          onSuccess={() => {
            setDaisyBillDocumentModalOpen(false);
            setSelectedBillForDocument(null);
            setSelectedVisitInfoForDocument(null);
          }}
          Loader={props.Loader}
          Toast={props.Toast}
        />
      )} */}
    </>
  );
};

export default WithRouter(BreadCrumbs);
